import { ReactTFC } from "../../../../../utils/fp/missing-ts-parts";
import React, { FormEventHandler, useCallback, useRef, useState } from "react";
import { css } from "@emotion/react";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../../../../styles/styles-css-variables";
import { FONTS } from "../../../../../styles/fonts";
import cn from "classnames";
import { ReactComponent as ArrowSvg } from "../../../../../assets/icons/Icon/DKT/Cart Coupon Code.svg";
import { SwarovskiRadio } from "../../../../../components/_shared-components/ui-elements/SwarovskiRadio";
import { PDPAvailabilityNotificationsTranslationsProps } from "./types";
import { CSS_MQ } from "../../../../../styles/breakpoints";

export const PDPAvailabilityNotificationForm: ReactTFC<{
  translations: PDPAvailabilityNotificationsTranslationsProps;
  privacyPolicyLink: string;
  onAvailabilitySubscription: (email: string) => Promise<void>;
}> = props => {
  const [hasFormError, setFormError] = useState(false);

  const onInputChange = useCallback(() => {
    setFormError(!isFormValid());
  }, []);

  function isFormValid(): { email: string } | undefined {
    const email = inputRef.current?.value;
    const hasCheckboxChecked = !!checkboxRef.current?.checked;

    return !!email && hasCheckboxChecked ? { email } : undefined;
  }

  const inputRef = useRef<HTMLInputElement | null>(null);
  const checkboxRef = useRef<HTMLInputElement | null>(null);

  const onSubmit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();

    const isValid = isFormValid();
    setFormError(!isValid);

    if (isValid) {
      void props.onAvailabilitySubscription(isValid.email);
    }
  };

  return (
    <form onSubmit={onSubmit} className={hasFormError ? "with-error" : undefined}>
      <div
        css={css`
          display: flex;
          align-items: center;

          margin-bottom: ${CSS_SPACER_SIZES.md};

          ${CSS_MQ.lowResNotebook} {
            margin-bottom: ${CSS_SPACER_SIZES.smd};
          }

          &:focus-within {
            > ._button {
              background-color: ${CSS_COLORS.Green};
              color: ${CSS_COLORS.White};
            }
          }
        `}
      >
        <input
          ref={inputRef}
          placeholder={props.translations.yourEmailAddressLabel.value}
          type="email"
          css={css`
            margin: 0;
            padding: 0 18px;
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
            border: 1px solid black;
            height: 50px;

            font-size: 16px;
            min-width: 12em;
            flex-grow: 1;
            flex-basis: 0;

            &:focus {
              outline: none !important;
              border: 1px solid ${CSS_COLORS.Green};
            }

            &::placeholder {
              color: ${CSS_COLORS.Grey3};
              ${FONTS.MinervaModern500};
            }
          `}
        />
        <button
          className={cn("_button")}
          type={"submit"}
          css={css`
            height: 50px;
            width: 100px;
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
            border-top: 1px solid black;
            border-bottom: 1px solid black;
            border-right: 1px solid black;

            display: flex;
            justify-content: center;
            align-items: center;

            color: ${CSS_COLORS.Green};
            background-color: ${CSS_COLORS.White};
          `}
        >
          <ArrowSvg
            css={css`
              width: 25px;
            `}
          />
        </button>
      </div>

      <SwarovskiRadio
        type={"checkbox"}
        inputProps={{
          // required: true,
          ref: checkboxRef,
          onChange: onInputChange,
        }}
        labelProps={{}}
        hasError={hasFormError && !checkboxRef.current?.checked}
        attachStyles={css`
          display: flex;
          align-items: flex-end;

          p {
            margin-top: 0;
            margin-bottom: 0;
            margin-left: 12px;

            color: black;
            font-weight: 300;

            display: inline;
          }
        `}
      >
        <p>
          <span>{props.translations.availabilityNotificationIAccept.value} </span>
          <a
            href={props.privacyPolicyLink}
            target={"_blank"}
            css={css`
              .disabled & {
                color: inherit;
              }

              color: ${CSS_COLORS.Green};

              font-weight: 500;
            `}
          >
            {props.translations.privacyPolicyLabel.value}
          </a>

          <span>.</span>
        </p>
      </SwarovskiRadio>
    </form>
  );
};
